/**
 * Hover for the desktop mega menu.
 *
 * Clicks and keys already open the panels natively (popovertarget). This module only adds what
 * HTML can't do across browsers yet (interestfor is Chromium-only): a mouse resting on a trigger
 * opens its panel, and leaving the trigger, the panel and the header closes it again. The scrim
 * (::backdrop) reports as the panel itself, so leaving the panel for the scrim is detected by
 * position. Touch and pen input are ignored; they keep the tap behavior.
 *
 * The triggers are as high as the header and touch each other, so a mouse crosses them on its
 * way to the browser's tabs, or from one trigger to the far side of its panel. A panel therefore
 * opens only once the mouse has settled on a trigger: it moved no more than SETTLE px during the
 * delay. Until then the check repeats.
 */

const OPEN_DELAY = 90;
const SWITCH_DELAY = 60;
const CLOSE_DELAY = 200;
const SETTLE = 8;

class HeaderMenu extends HTMLElement {
  #header = null;
  #openTimer = 0;
  #closeTimer = 0;
  #hoverPanel = null;
  #pointer = { x: 0, y: 0 };
  // Below its breakpoint the menu is not rendered (the drawer stands in). A panel that is open at
  // that moment (window resized, tablet turned) would stay open unseen and keep the page locked.
  #resize = new ResizeObserver(() => {
    if (!this.checkVisibility()) this.#openPanel?.hidePopover();
  });

  connectedCallback() {
    this.#header = this.closest(".header") ?? this;
    this.#header.addEventListener("pointerover", this.#onOver);
    this.#header.addEventListener("pointermove", this.#onMove);
    this.#header.addEventListener("pointerleave", this.#onLeave);
    this.addEventListener("click", this.#onClick);
    this.addEventListener("toggle", this.#onToggle, true);
    this.#resize.observe(this);
  }

  disconnectedCallback() {
    this.#resize.disconnect();
    this.#header.removeEventListener("pointerover", this.#onOver);
    this.#header.removeEventListener("pointermove", this.#onMove);
    this.#header.removeEventListener("pointerleave", this.#onLeave);
    this.removeEventListener("click", this.#onClick);
    this.removeEventListener("toggle", this.#onToggle, true);
    this.#clearTimers();
  }

  get #openPanel() {
    return this.querySelector(".header-mega:popover-open");
  }

  #panelFor(trigger) {
    return document.getElementById(trigger.getAttribute("popovertarget"));
  }

  #onOver = (event) => {
    if (event.pointerType !== "mouse") return;
    const trigger = event.target.closest(".nav-trigger");
    if (trigger && this.contains(trigger)) {
      // The label and the space around it are two targets: moving between them is no new entry.
      if (trigger.contains(event.relatedTarget)) return;
      clearTimeout(this.#closeTimer);
      clearTimeout(this.#openTimer);
      this.#pointer = { x: event.clientX, y: event.clientY };
      this.#openWhenSettled(trigger);
      return;
    }
    clearTimeout(this.#openTimer);
    if (event.target.closest(".header-mega")) return;
    if (this.#hoverPanel) this.#scheduleClose();
  };

  #onMove = (event) => {
    if (event.pointerType !== "mouse") return;
    this.#pointer = { x: event.clientX, y: event.clientY };
    if (!this.#hoverPanel) return;
    const panel = event.target.closest(".header-mega");
    if (!panel) return;
    const rect = panel.getBoundingClientRect();
    const onScrim = event.clientY > rect.bottom || event.clientY < rect.top;
    if (onScrim) this.#scheduleClose();
    else clearTimeout(this.#closeTimer);
  };

  #onLeave = (event) => {
    if (event.pointerType !== "mouse") return;
    clearTimeout(this.#openTimer);
    if (this.#hoverPanel) this.#scheduleClose();
  };

  // A click on the trigger of a panel the mouse already opened keeps it open instead of
  // toggling it shut. The panel stays hover-driven: leaving it still closes it.
  #onClick = (event) => {
    const trigger = event.target.closest(".nav-trigger");
    if (!trigger || event.pointerType !== "mouse") return;
    const panel = this.#panelFor(trigger);
    if (panel && panel === this.#hoverPanel && panel.matches(":popover-open")) {
      event.preventDefault();
    }
  };

  #onToggle = (event) => {
    if (event.newState === "closed" && event.target === this.#hoverPanel) this.#hoverPanel = null;
  };

  #openWhenSettled(trigger) {
    const from = this.#pointer;
    const delay = this.#openPanel ? SWITCH_DELAY : OPEN_DELAY;
    this.#openTimer = setTimeout(() => {
      const moved = Math.hypot(this.#pointer.x - from.x, this.#pointer.y - from.y);
      if (moved > SETTLE) this.#openWhenSettled(trigger);
      else this.#show(this.#panelFor(trigger));
    }, delay);
  }

  #show(panel) {
    if (!panel || panel.matches(":popover-open")) return;
    panel.showPopover();
    this.#hoverPanel = panel;
  }

  #scheduleClose() {
    clearTimeout(this.#closeTimer);
    this.#closeTimer = setTimeout(() => {
      if (this.#hoverPanel?.matches(":popover-open")) this.#hoverPanel.hidePopover();
      this.#hoverPanel = null;
    }, CLOSE_DELAY);
  }

  #clearTimers() {
    clearTimeout(this.#openTimer);
    clearTimeout(this.#closeTimer);
  }
}

customElements.define("header-menu", HeaderMenu);
